import { useEffect, useState } from 'react'
import {
  LayoutDashboard, Users, ShieldCheck, Laptop, MessageSquare, Server,
  ExternalLink, CheckCircle2, Clock, AlertTriangle, PowerOff, Wrench, PlugZap, Moon,
} from 'lucide-react'
import { Shell } from './admin/Shell.js'
import { usePrefsStore } from './prefs.js'
import { FreshnessLine, LiveNumber, QuietNumber, type SoireeView, type TicketingFreshness } from './soirees/lib.js'
import { NuitBilletteries } from './soirees/NuitBilletteries.js'
import { afficherLaNuit, grandNombre, type AffichageBilletteries, type VueNuit } from './soirees/nuit-billetteries-model.js'
import { RattachementsAVenirPanel } from './soirees/Rattachements.js'
import { barSurAccueil, ligneBarCourte, type VenueFormules } from './soirees/formules-model.js'
import { useMenuCarteAccueil } from './layout/menu-tuiles.js'

/**
 * ACCUEIL GLOBAL — le vrai « / » de Gestion (LOT 3).
 *
 * Une seule requête logique : GET /api/v1/dashboard (qui ne lit que
 * PostgreSQL). Le navigateur ne parle JAMAIS à la Main Courante ni au Parc —
 * leurs cartes s'appuient sur le cache service_summary entretenu par le
 * worker. Fond LIVE CONTROL standard : le réseau de particules reste
 * réservé au parcours d'authentification.
 */

/* ─── modèle renvoyé par l'API (déjà taillé pour l'affichage) ─── */
interface Metric { key: string; label: string; value: number | string; unit: string | null; severity: string }
interface Card {
  service_key: string; name: string; category: string; state: string
  service_status: string | null
  headline: { severity: string; title: string; detail: string | null } | null
  metrics: Metric[]
  top_alert: { severity: string; title: string; url: string | null } | null
  primary_action: { label: string; url: string } | null
  stale: boolean
  last_success_at: string | null
  seconds_since_success: number | null
  last_error_class: string | null
}
interface Attention { service_key: string; service_name: string; category: string; severity: string; title: string; url: string | null }
interface RhSummary { state: string; headline: string; detail: string; metrics: { key: string; label: string; value: number }[] }
interface InfraSummary {
  level: 'OPERATIONAL' | 'ATTENTION' | 'DEGRADED'
  total: number; healthy: number; attention: number
  lines: { key: string; label: string; state: string; state_label: string }[]
}
interface DashboardData {
  generated_at: string; services_total: number; services_fresh: number
  alerts_active_unacked: number
  cards: Card[]; attention: Attention[]; rh: RhSummary
  infrastructure?: InfraSummary
}

/* ─── familles affichées, dans l'ordre du produit ─── */
const FAMILLES = [
  { category: 'SECURITY', libelle: 'Sécurité', Icone: ShieldCheck, tint: 'violet' },
  { category: 'PARC', libelle: 'Parc', Icone: Laptop, tint: 'cyan' },
  { category: 'RH_ACCESS', libelle: 'RH', Icone: Users, tint: 'emerald' },
  { category: 'INFRASTRUCTURE', libelle: 'Infrastructure', Icone: Server, tint: 'neutral' },
  { category: 'COMMUNICATION', libelle: 'Communication', Icone: MessageSquare, tint: 'neutral' },
] as const

/**
 * VALEUR DE KPI — un fondu quand elle change VRAIMENT, rien de plus.
 *
 * Surtout pas de comptage 0 → valeur : « 493 soirées » est un total
 * historique, le faire défiler laisserait croire que 493 choses viennent de
 * se produire (§18). Les valeurs non numériques (« 3/4 », « OK ») passent
 * telles quelles : il n'y a rien à animer honnêtement.
 */
function KpiValue({ value }: { value: number | string }) {
  if (typeof value !== 'number') return <>{value}</>
  return <QuietNumber value={value} />
}

function ageCourt(seconds: number | null): string {
  if (seconds === null) return '—'
  if (seconds < 60) return `il y a ${seconds} s`
  if (seconds < 3600) return `il y a ${Math.round(seconds / 60)} min`
  return `il y a ${Math.round(seconds / 3600)} h`
}

/** L'état d'une carte décide sa teinte : l'urgence prime sur la famille. */
function tintFor(state: string, familyTint: string): string {
  if (state === 'CRITICAL' || state === 'DOWN') return 'rose'
  if (state === 'ACTION_REQUIRED' || state === 'MAINTENANCE' || state === 'STALE' || state === 'WARNING') return 'amber'
  return familyTint
}

function StatePill({ state, stale }: { state: string; stale: boolean }) {
  if (state === 'DOWN') return <span className="lc-pill lc-pill--error"><PowerOff size={13} />Indisponible</span>
  if (state === 'CRITICAL') return <span className="lc-pill lc-pill--error"><AlertTriangle size={13} />Critique</span>
  if (state === 'MAINTENANCE') return <span className="lc-pill lc-pill--warning"><Wrench size={13} />Maintenance</span>
  if (state === 'STALE' || stale) return <span className="lc-pill lc-pill--warning"><Clock size={13} />Données périmées</span>
  if (state === 'ACTION_REQUIRED') return <span className="lc-pill lc-pill--warning"><AlertTriangle size={13} />À traiter</span>
  if (state === 'WARNING') return <span className="lc-pill lc-pill--warning"><AlertTriangle size={13} />Attention</span>
  if (state === 'NO_DATA') return <span className="lc-pill lc-pill--idle"><PlugZap size={13} />Aucune donnée</span>
  if (state === 'NOT_CONFIGURED') return <span className="lc-pill lc-pill--idle">Non configurée</span>
  return <span className="lc-pill lc-pill--ok"><span className="lc-dot lc-dot--ok lc-dot--live" />Opérationnel</span>
}

/* ═══════════════════════════ carte d'un service externe ═══════════════ */

function ServiceCardView({ card, famille }: { card: Card; famille: (typeof FAMILLES)[number] }) {
  const tint = tintFor(card.state, famille.tint)
  const p = card.headline
  const { Icone } = famille
  const menu = useMenuCarteAccueil()

  return (
    <article className={`lc-card lc-card--${tint} gd-card`} {...menu({ sorte: 'service', url: card.primary_action?.url ?? null }, card.name)}>
      <span className="lc-card__edge" aria-hidden="true" />
      <div className="gd-card__top">
        <span className="lc-card__icon" aria-hidden="true"><Icone /></span>
        <StatePill state={card.state} stale={card.stale} />
      </div>
      <div className="gd-card__family">
        {famille.libelle.toLowerCase() === card.name.toLowerCase()
          ? famille.libelle
          : `${famille.libelle} · ${card.name}`}
      </div>

      {card.state === 'NO_DATA' ? (
        <>
          <div className="lc-card__value--word">Service indisponible</div>
          <p className="lc-card__desc">
            Aucune donnée fiable disponible.
            {card.last_error_class === 'AUTHENTICATION_ERROR'
              ? ' Connexion au service impossible.'
              : ' Gestion réessaie automatiquement.'}
          </p>
        </>
      ) : card.state === 'DOWN' ? (
        <>
          <div className="lc-card__value--word">{card.name} indisponible</div>
          <p className="lc-card__desc">
            Dernier contrôle : {ageCourt(card.seconds_since_success)}.
            Gestion affiche le dernier état connu et réessaie automatiquement.
          </p>
        </>
      ) : (
        <>
          <div className="lc-card__value--word">{p?.title ?? '—'}</div>
          {p?.detail ? <p className="lc-card__desc">{p.detail}</p> : null}
          {card.stale ? (
            <p className="gd-stale">
              <Clock size={13} aria-hidden="true" />
              Dernières données fiables : {ageCourt(card.seconds_since_success)}
            </p>
          ) : null}
        </>
      )}

      {card.metrics.length > 0 && card.state !== 'NO_DATA' ? (
        <div className="gd-metrics">
          {card.metrics.map((m) => (
            <span key={m.key} className="gd-metric" data-severity={m.severity}>
              <strong><KpiValue value={m.value} />{m.unit ?? ''}</strong>
              <span>{m.label}</span>
            </span>
          ))}
        </div>
      ) : null}

      <span className="lc-card__spacer" />

      {card.primary_action ? (
        <a className="lc-card__cta" href={card.primary_action.url} target="_blank" rel="noopener noreferrer">
          {card.primary_action.label}
          <ExternalLink size={16} aria-hidden="true" />
        </a>
      ) : null}
    </article>
  )
}

/* ═══════════════════════════ carte RH (interne à Gestion) ═════════════ */

function RhCardView({ rh }: { rh: RhSummary }) {
  const tint = rh.state === 'ACTION_REQUIRED' ? 'amber' : 'emerald'
  const menu = useMenuCarteAccueil()
  return (
    <article className={`lc-card lc-card--${tint} gd-card`} {...menu({ sorte: 'rh' }, 'RH')}>
      <span className="lc-card__edge" aria-hidden="true" />
      <div className="gd-card__top">
        <span className="lc-card__icon" aria-hidden="true"><Users /></span>
        {rh.state === 'NOT_CONFIGURED'
          ? <span className="lc-pill lc-pill--idle">Connecteurs à venir</span>
          : rh.state === 'ACTION_REQUIRED'
            ? <span className="lc-pill lc-pill--warning"><AlertTriangle size={13} />À traiter</span>
            : <span className="lc-pill lc-pill--ok"><span className="lc-dot lc-dot--ok" />Synchronisé</span>}
      </div>
      <div className="gd-card__family">RH · Gestion</div>
      <div className="lc-card__value--word">{rh.headline}</div>
      <p className="lc-card__desc">{rh.detail}</p>
      <div className="gd-metrics">
        {rh.metrics.map((m) => (
          <span key={m.key} className="gd-metric"><strong><KpiValue value={m.value} /></strong> {m.label}</span>
        ))}
      </div>
      <span className="lc-card__spacer" />
      <a className="lc-card__cta" href="/equipe/rh">
        Ouvrir RH
        <LayoutDashboard size={16} aria-hidden="true" />
      </a>
    </article>
  )
}

/* ═══════════════ carte Infrastructure (LOT 8 — moteur commun) ═════════ */

function InfraCardView({ infra }: { infra: InfraSummary }) {
  const attention = infra.lines.filter((l) => l.state !== 'HEALTHY')
  const tint = infra.level === 'DEGRADED' ? 'rose' : infra.level === 'ATTENTION' ? 'amber' : 'neutral'
  const menu = useMenuCarteAccueil()
  return (
    <article className={`lc-card lc-card--${tint} gd-card`} {...menu({ sorte: 'infrastructure' }, 'Infrastructure')}>
      <span className="lc-card__edge" aria-hidden="true" />
      <div className="gd-card__top">
        <span className="lc-card__icon" aria-hidden="true"><Server /></span>
        {infra.level === 'OPERATIONAL'
          ? <span className="lc-pill lc-pill--ok"><span className="lc-dot lc-dot--ok lc-dot--live" />Opérationnel</span>
          : infra.level === 'ATTENTION'
            ? <span className="lc-pill lc-pill--warning"><AlertTriangle size={13} />Attention</span>
            : <span className="lc-pill lc-pill--error"><AlertTriangle size={13} />Dégradé</span>}
      </div>
      <div className="gd-card__family">Infrastructure</div>
      <div className="lc-card__value--word">
        {infra.attention === 0
          ? `${infra.healthy} systèmes opérationnels`
          : `${infra.attention} système${infra.attention > 1 ? 's' : ''} à surveiller`}
      </div>
      <p className="lc-card__desc">
        {infra.attention === 0
          ? 'Serveur, réseau, vidéo, domotique et sauvegardes répondent normalement.'
          : attention.map((l) => `${l.label} : ${l.state_label.toLowerCase()}`).slice(0, 2).join(' · ')}
      </p>
      <span className="lc-card__spacer" />
      <a className="lc-card__cta" href="/infrastructure">Ouvrir l’infrastructure</a>
    </article>
  )
}

/* ═══════════════════════════ famille non configurée ═══════════════════ */

function NotConfiguredCard({ famille }: { famille: (typeof FAMILLES)[number] }) {
  const { Icone } = famille
  const menu = useMenuCarteAccueil()
  return (
    <article className="lc-card gd-card gd-card--muted" {...menu({ sorte: 'non-configuree', famille: famille.category }, famille.libelle)}>
      <div className="gd-card__top">
        <span className="lc-card__icon gd-icon--muted" aria-hidden="true"><Icone /></span>
        <StatePill state="NOT_CONFIGURED" stale={false} />
      </div>
      <div className="gd-card__family">{famille.libelle}</div>
      <div className="lc-card__value--word gd-muted-title">Intégration non configurée</div>
      <p className="lc-card__desc">Cette famille sera branchée dans un lot ultérieur.</p>
      <span className="lc-card__spacer" />
      <span className="lc-card__cta gd-cta--muted" aria-disabled="true">Configurer plus tard</span>
    </article>
  )
}

/* ═══════════════ carte Communication (V1 — par canal, §54) ════════════ */

interface CommTuile {
  configured: boolean
  partial: boolean
  lines: { key: string; label: string; value: string; hint: string | null }[]
}

/**
 * Des indicateurs PAR CANAL — jamais un « +8,4 % global » qui additionnerait
 * des pourcentages incomparables (abonnés Instagram vs impressions Google).
 * Non configurée → la même carte muette que les autres familles ; connectée
 * mais sans historique → la ligne le dit, sans faux « +0 % ».
 */
function CommunicationCardView() {
  const [tuile, setTuile] = useState<CommTuile | null>(null)
  const [indisponible, setIndisponible] = useState(false)
  const menu = useMenuCarteAccueil()
  useEffect(() => {
    let vivant = true
    fetch('/api/v1/communication/dashboard-tile', { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((j: CommTuile & { error?: string }) => {
        if (!vivant) return
        if (j.error || !Array.isArray(j.lines)) setIndisponible(true)
        else setTuile(j)
      })
      .catch(() => { if (vivant) setIndisponible(true) })
    return () => { vivant = false }
  }, [])

  const famille = FAMILLES.find((f) => f.category === 'COMMUNICATION')!
  if (indisponible || (tuile && !tuile.configured)) return <NotConfiguredCard famille={famille} />
  return (
    <article className="lc-card lc-card--neutral gd-card" {...menu({ sorte: 'communication' }, 'Communication')}>
      <span className="lc-card__edge" aria-hidden="true" />
      <div className="gd-card__top">
        <span className="lc-card__icon" aria-hidden="true"><MessageSquare /></span>
        {tuile === null ? null
          : tuile.partial
            ? <span className="lc-pill lc-pill--idle">Partiel</span>
            : <span className="lc-pill lc-pill--ok"><span className="lc-dot lc-dot--ok lc-dot--live" />Connecté</span>}
      </div>
      <div className="gd-card__family">Communication</div>
      {tuile === null ? (
        <p className="lc-card__desc">Chargement…</p>
      ) : tuile.lines.length === 0 ? (
        <p className="lc-card__desc">Canaux reliés — les premiers indicateurs arrivent avec l’historique.</p>
      ) : (
        <dl className="gd-comm-lines">
          {tuile.lines.map((l) => (
            <div key={l.key}>
              <dt>{l.label}</dt>
              <dd>{l.value}{l.hint ? <span className="lc-hint"> · {l.hint}</span> : null}</dd>
            </div>
          ))}
        </dl>
      )}
      <span className="lc-card__spacer" />
      <a className="lc-card__cta" href="/communication">Ouvrir la communication</a>
    </article>
  )
}

/* ═══════════════════ carte « Ce soir » (domaine Soirées) ══════════════ */

/**
 * Soirée EN COURS uniquement — le Dashboard reste calme le reste du temps.
 * « Billets scannés », jamais « présents » : les sorties ne sont pas
 * comptabilisées. Sans permission soiree.read, la carte reste au placeholder.
 */
interface CeSoir {
  soiree: SoireeView | null
  freshness: TicketingFreshness
  /** La nuit, toutes billetteries (Shotgun + associations) — 16/10/2026. */
  night?: VueNuit | null
  billetteries?: AffichageBilletteries
  /** « Formules pour le bar » de la soirée en cours (19/10/2026) ; `null` sans billet d'association. */
  formules?: VenueFormules | null
}

function TonightCard() {
  const [data, setData] = useState<CeSoir | null>(null)
  const [interdit, setInterdit] = useState(false)

  useEffect(() => {
    let vivant = true
    const charger = () => {
      fetch('/api/v1/soirees/current', { credentials: 'same-origin' })
        .then((r) => {
          if (r.status === 403) { setInterdit(true); return Promise.reject(new Error('403')) }
          return r.ok ? r.json() : Promise.reject(new Error(String(r.status)))
        })
        .then((j: CeSoir) => { if (vivant) setData(j) })
        .catch(() => undefined)
    }
    charger()
    const t = setInterval(charger, 60_000)
    return () => { vivant = false; clearInterval(t) }
  }, [])
  const menu = useMenuCarteAccueil()

  const s = data?.soiree && data.soiree.phase === 'ONGOING' ? data.soiree : null
  if (s) {
    // Réglages Soirées → Billetteries : la nuit entière (club + associations)
    // quand elle dit plus que la soirée, et si le tableau de bord la montre.
    const grand = grandNombre(s.tickets_scanned ?? null, data?.night, data?.billetteries, 'dashboard')
    const nuit = afficherLaNuit(data?.night, data?.billetteries, 'dashboard') ? data?.night ?? null : null
    return (
      <article className="lc-card lc-card--violet gd-card gd-card--cesoir" {...menu({ sorte: 'ce-soir', soireeId: s.id, interdit: false }, s.name)}>
        <span className="lc-card__edge" aria-hidden="true" />
        <div className="gd-card__top">
          <span className="lc-card__icon" aria-hidden="true"><Moon /></span>
          <span className="lc-pill lc-pill--ok"><span className="lc-dot lc-dot--ok lc-dot--live" />En cours</span>
        </div>
        <div className="gd-card__family">Soirée en cours</div>
        <div className="lc-card__value--word">{s.name}</div>
        {/* « Commence à 23 h 55 · MEDECINE déjà ouverte » : la porte scanne déjà pour un événement rattaché. */}
        {s.ouverte_avant ? <p className="lc-hint">{s.ouverte_avant.libelle}</p> : null}
        {/* La seule donnée réellement vivante du tableau de bord : elle a
            droit au relais du grand nombre (nouvelle valeur qui remplace
            l'ancienne), jamais à un comptage. */}
        <p className="lc-card__desc">
          {grand.estLaNuit
            ? <><LiveNumber value={grand.valeur} /> entrée{(grand.valeur ?? 0) > 1 ? 's' : ''} cette nuit</>
            : <><LiveNumber value={s.tickets_scanned ?? null} /> billet{(s.tickets_scanned ?? 0) > 1 ? 's' : ''} scanné{(s.tickets_scanned ?? 0) > 1 ? 's' : ''}</>}
        </p>
        {nuit ? <NuitBilletteries nuit={nuit} sansTete={grand.estLaNuit} /> : null}
        {/* Le bar en direct : les formules des billets d'associations déjà entrées. */}
        {/* Des chiffres Pretix : soumis au réglage « tableau de bord », comme la nuit au-dessus. */}
        {data && barSurAccueil(data.formules, data.billetteries) ? <p className="lc-hint gd-cesoir__bar"><strong>Bar</strong> · {ligneBarCourte(data.formules.formules)}</p> : null}
        <FreshnessLine f={data?.freshness ?? null} />
        <span className="lc-card__spacer" />
        <a className="lc-card__cta" href={`/soirees/${s.id}`}>Ouvrir la soirée</a>
      </article>
    )
  }
  return (
    <article className="lc-card gd-card gd-card--muted gd-card--cesoir" {...menu({ sorte: 'ce-soir', soireeId: null, interdit }, 'Ce soir')}>
      <div className="gd-card__top">
        <span className="lc-card__icon gd-icon--muted" aria-hidden="true"><Moon /></span>
        <span className="lc-pill lc-pill--idle">Calme</span>
      </div>
      <div className="gd-card__family">Ce soir</div>
      <div className="lc-card__value--word gd-muted-title">Aucune soirée en cours</div>
      <p className="lc-card__desc">
        {interdit
          ? 'La vue soirée nécessite un accès dédié.'
          : 'La carte s’animera automatiquement quand une soirée démarrera.'}
      </p>
      <span className="lc-card__spacer" />
      {!interdit ? <a className="lc-card__cta" href="/soirees">Ouvrir les soirées</a> : null}
    </article>
  )
}

/* ═══════════════════════════ page complète ════════════════════════════ */

export function GlobalDashboardPage() {
  const [data, setData] = useState<DashboardData | null>(null)
  const [erreur, setErreur] = useState(false)
  // Préférences non destructives du LOT 5.5 : masquer « Ce soir » et les
  // familles non configurées — le contenu LOT 3 lui-même ne change pas.
  const { resolved } = usePrefsStore()
  const showTonight = resolved['dashboard_show_tonight'] !== 'false'
  const showUnconfigured = resolved['dashboard_show_unconfigured'] !== 'false'

  useEffect(() => {
    let vivant = true
    const charger = () => {
      fetch('/api/v1/dashboard', { credentials: 'same-origin' })
        .then((res) => res.json())
        .then((j: DashboardData) => { if (vivant) { setData(j); setErreur(false) } })
        .catch(() => { if (vivant) setErreur(true) })
    }
    charger()
    // Rythme calme : le cache PostgreSQL est rafraîchi par le worker ;
    // recharger plus vite n'apporterait rien.
    const t = setInterval(charger, 60_000)
    return () => { vivant = false; clearInterval(t) }
  }, [])

  const cardFor = (category: string) => data?.cards.find((c) => c.category === category) ?? null
  const attention = data?.attention ?? []

  return (
    <Shell
      active="dashboard"
      meta={{
        servicesFresh: data?.services_fresh ?? null,
        servicesTotal: data?.services_total ?? null,
        alertsBadge: data?.alerts_active_unacked ?? 0,
        tempsBadge: 0,
      }}
    >
      {/* ENTRÉE DU TABLEAU DE BORD (§17).
          Les GRANDES ZONES arrivent avec un décalage court — titre, à traiter,
          modules — puis les cartes à l'intérieur, plus court encore. On doit
          voir une composition s'installer, pas six widgets défiler. */}
      <main className="lc-page lc-stagger gd-page">
        <h1 className="lc-title">Tableau de bord</h1>
        <p className="lc-subtitle">
          {attention.length > 0
            ? 'Voici ce qui demande votre attention maintenant.'
            : 'Tout est calme. Voici l’état du Live Club en ce moment.'}
        </p>

        {erreur && !data ? (
          <section className="lc-panel gd-panel">
            <div className="lc-panel__body gd-error">
              Le tableau de bord est momentanément indisponible.
              Gestion réessaie automatiquement.
            </div>
          </section>
        ) : null}

      {/* ═══ À TRAITER ═══ */}
              {attention.length > 0 ? (
                <section className="lc-panel gd-panel" aria-labelledby="a-traiter">
                  <header className="lc-panel__head">
                    <h2 className="lc-section-title" id="a-traiter">À traiter</h2>
                  </header>
                  <div className="lc-panel__body lc-panel__body--flush">
                    {attention.map((a) => (
                      <div
                        key={`${a.service_key}-${a.title}`}
                        className={`lc-action-row ${a.severity === 'CRITICAL' || a.severity === 'ERROR' || a.severity === 'DOWN' ? 'lc-action-row--critical' : 'lc-action-row--action'}`}
                      >
                        <span className="lc-action-row__icon" aria-hidden="true">
                          {a.category === 'SECURITY' ? <ShieldCheck size={20} /> : a.category === 'PARC' ? <Laptop size={20} /> : <AlertTriangle size={20} />}
                        </span>
                        <div className="lc-action-row__body">
                          <div className="lc-action-row__title">{a.title}</div>
                          <div className="lc-action-row__meta">{a.service_name}</div>
                        </div>
                        {a.url ? (
                          <a className="lc-btn" href={a.url} target="_blank" rel="noopener noreferrer">
                            Traiter
                            <ExternalLink size={14} aria-hidden="true" />
                          </a>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </section>
              ) : data ? (
                <p className="gd-calm">
                  <CheckCircle2 size={15} aria-hidden="true" />
                  Aucune action urgente
                </p>
              ) : null}

              {/* ═══ RATTACHEMENTS ANNONCÉS (19/10/2026) — « MEDECINE le 15/10 sera
                  rattachée à L'ÉTUDIANTE », corrigeable d'un clic avant la nuit.
                  Rien quand il n'y a rien à annoncer, ou sans accès aux soirées. ═══ */}
              <RattachementsAVenirPanel lieu="accueil" />

              {/* ═══ MODULES ═══ */}
              <div className="lc-cards gd-cards lc-stagger">
                {FAMILLES.map((f) => {
                  if (f.category === 'RH_ACCESS') {
                    return data ? <RhCardView key={f.category} rh={data.rh} /> : null
                  }
                  if (f.category === 'INFRASTRUCTURE' && data?.infrastructure) {
                    return <InfraCardView key={f.category} infra={data.infrastructure} />
                  }
                  // COMMUNICATION V1 — la tuile lit sa propre source
                  // (PostgreSQL via l'API), par canal, sans % global.
                  if (f.category === 'COMMUNICATION') {
                    return <CommunicationCardView key={f.category} />
                  }
                  const card = cardFor(f.category)
                  if (card) return <ServiceCardView key={f.category} card={card} famille={f} />
                  return showUnconfigured ? <NotConfiguredCard key={f.category} famille={f} /> : null
                })}

                {/* ═══ CE SOIR — la soirée en cours (domaine Soirées) ═══ */}
                {showTonight ? <TonightCard /> : null}
              </div>
        </main>
    </Shell>
  )
}
